/**
 * MaterialBottomNav - Material Design 3 Bottom Navigation Bar
 * Mobile-first, app-like design optimized for low-tech users
 * 
 * Features:
 * - Fixed at bottom with Material elevation (8dp)
 * - 4 main tabs with icons and labels
 * - Active state indicator
 * - Touch-friendly (48dp minimum)
 * - Smooth transitions
 */

import React, { memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, BookOpen, BarChart3, Calendar, Bell } from 'lucide-react';
import { getIconSize } from '../../common/mobile/IconSizes';

/**
 * Navigation items configuration
 * Simple, clear labels for low-tech users
 */
const navigationItems = [
  {
    id: 'home',
    label: 'Home',
    icon: Home,
    path: '/teacher',
    ariaLabel: 'Go to dashboard',
  },
  {
    id: 'teaching',
    label: 'Teaching',
    icon: BookOpen,
    path: '/teacher/teaching',
    ariaLabel: 'Manage classes and content',
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: BarChart3,
    path: '/teacher/reports',
    ariaLabel: 'View progress reports',
  },
  {
    id: 'schedule',
    label: 'Schedule',
    icon: Calendar,
    path: '/teacher/schedule',
    ariaLabel: 'View daily schedule',
  },
  {
    id: 'notifications',
    label: 'Alerts',
    icon: Bell,
    path: '/teacher/notifications',
    ariaLabel: 'View notifications',
  },
];

/**
 * MaterialBottomNav Component
 * @param {Object} props
 * @param {Function} props.onProfileClick - Optional: Opens drawer instead of navigating (if provided)
 */
const MaterialBottomNav = memo(function MaterialBottomNav({ onProfileClick, unreadCount = 0 }) {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Check if a navigation item is active
   * Exact match for home, prefix match for others
   */
  const isActive = (path) => {
    if (path === '/teacher') {
      // Home is active for Dashboard AND ClassHub (detail view)
      return location.pathname === '/teacher' ||
        location.pathname === '/teacher/' ||
        location.pathname.startsWith('/teacher/class/');
    }
    return location.pathname.startsWith(path);
  };

  /**
   * Handle navigation item click
   */
  const handleClick = (item) => {
    // If it's profile and onProfileClick is provided, open drawer instead
    if (item.id === 'profile' && onProfileClick) {
      onProfileClick();
      return;
    }

    // Otherwise, navigate to the path
    navigate(item.path);
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-bottom-nav bg-white border-t border-gray-200/60 shadow-md-elevation-8"
      style={{
        height: '64px', // Material Design standard (comfortable touch target)
      }}
      role="navigation"
      aria-label="Bottom navigation"
    >
      <div className="flex items-center justify-around h-full px-2">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <button
              key={item.id}
              onClick={() => handleClick(item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick(item);
                }
              }}
              className={`
                flex flex-col items-center justify-center
                flex-1 h-full
                min-h-touch-min min-w-touch-min
                transition-all duration-300 ease-out
                gap-1
                focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg
                ${active
                  ? 'text-primary-500'
                  : 'text-text-secondary hover:text-text-primary'
                }
              `}
              aria-label={item.ariaLabel}
              aria-current={active ? 'page' : undefined}
            >
              {/* MD3 Active Indicator Pill - Enhanced */}
              <div className={`
                  relative flex items-center justify-center
                  px-3 py-1.5 rounded-full transition-all duration-300 ease-out
                  ${active
                    ? 'bg-primary-500 scale-105'
                    : 'bg-transparent hover:bg-gray-100'
                  }
              `}>
                <Icon
                  size={getIconSize('large')}
                  className={`
                      transition-all duration-300
                      ${active ? 'text-white' : 'text-text-secondary'}
                    `}
                  strokeWidth={active ? 2.5 : 2}
                />
                {/* Badge for Alerts tab */}
                {item.id === 'notifications' && unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>

              {/* Label */}
              <span
                className={`
                  text-mobile-body-sm transition-all duration-300
                  ${active ? 'text-primary-500 font-bold' : 'text-text-secondary font-medium'}
                `}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
});

export default MaterialBottomNav;

